import { MicrositeTemplate } from '~/model';
import { useRegisterSection } from '~/hooks/useSections';
import MatterportBlockAlpha from '~/editor/templates/alpha/MatterportBlockAlpha';
import MatterportBlockDelta from '~/editor/templates/delta/MatterportBlockDelta';
import MatterportBlockEpsilon from '~/editor/templates/epsilon/MatterportBlockEpsilon';
import MatterportBlockEta from '~/editor/templates/eta/MatterportBlockEta';
import MatterportBlockZeta from '~/editor/templates/zeta/MatterportBlockZeta';
import { type ComponentConfig } from '@puckeditor/core';
import { useEffect, useRef, useState } from 'react';
import useIsTab from '~/hooks/useIsTab';
import MicrositeRatio from '~/components/MicrositeRatio';
import styled from '@emotion/styled';
import MatterportBlockBeta from '~/editor/templates/beta/MatterportBlockBeta';
const DEMO_MATTERPORT_URL = 'https://my.matterport.com/show/?m=ymFqsnzzWUg';

export interface MatterportBlockProps {
	navigation?: boolean;
	template: MicrositeTemplate;
}

export const PUCK_BLOCK_MATTERPORT: ComponentConfig<MatterportBlockProps> = {
	label: 'Matterport',
	fields: {
		navigation: {
			type: 'radio',
			options: [
				{ label: 'On', value: true },
				{ label: 'Off', value: false },
			],
		},
		template: {
			type: 'radio',
			options: [
				{ label: 'Alpha', value: MicrositeTemplate.ALPHA },
				{ label: 'Beta', value: MicrositeTemplate.BETA },
				{ label: 'Delta', value: MicrositeTemplate.DELTA },
				{ label: 'Epsilon', value: MicrositeTemplate.EPSILON },
				{ label: 'Eta', value: MicrositeTemplate.ETA },
				{ label: 'Zeta', value: MicrositeTemplate.ZETA },
			],
		},
	},
	defaultProps: {
		navigation: true,
		template: MicrositeTemplate.ETA,
	},
	permissions: {
		duplicate: false,
	},
	render(props) {
		return <MatterportBlock {...props} />;
	},
};

const MatterportIframe = styled.iframe`
	position: absolute;
	width: 100%;
	height: 100%;
	border: 0;
`;

export default function MatterportBlock({ navigation = true, template }: MatterportBlockProps) {
	const id = useRegisterSection('matterport', '3D Walkthrough', navigation);
	const ref = useRef<HTMLDivElement>(null);
	const [render, setRender] = useState(true);
	const isTab = useIsTab();

	if (isTab) {
		return (
			<MicrositeRatio id={id}>
				<MatterportIframe src={DEMO_MATTERPORT_URL} allowFullScreen allow="xr-spatial-tracking" />
			</MicrositeRatio>
		);
	}

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;

		const resizeStart = () => {
			if (render) {
				setRender(false);
			}

			if (timeout) {
				clearTimeout(timeout);
			}

			timeout = setTimeout(() => {
				setRender(true);
			}, 200);
		};

		window.addEventListener('resize', resizeStart);

		return () => {
			window.removeEventListener('resize', resizeStart);
		};
	}, [render]);

	let content = <></>;

	if (template === MicrositeTemplate.ALPHA) {
		content = <MatterportBlockAlpha url={DEMO_MATTERPORT_URL} />;
	}
	if (template === MicrositeTemplate.BETA) {
		content = <MatterportBlockBeta url={DEMO_MATTERPORT_URL} />;
	}
	if (template === MicrositeTemplate.DELTA) {
		content = <MatterportBlockDelta url={DEMO_MATTERPORT_URL} />;
	}
	if (template === MicrositeTemplate.EPSILON) {
		content = <MatterportBlockEpsilon url={DEMO_MATTERPORT_URL} />;
	}
	if (template === MicrositeTemplate.ETA) {
		content = <MatterportBlockEta url={DEMO_MATTERPORT_URL} />;
	}
	if (template === MicrositeTemplate.ZETA) {
		content = <MatterportBlockZeta url={DEMO_MATTERPORT_URL} />;
	}

	return (
		<div id={id} ref={ref} style={{ visibility: render ? 'visible' : 'hidden' }}>
			{content}
		</div>
	);
}
